/**
 * StoryBlock — narrative feature story with a cyan accent header + side image
 * (2726 "Reborn Cyan"). Image failures fall back to the guide default image
 * (CX-2725 AC).
 *
 * NOTE: `story.callout` is rendered by the parent (ChapterSubPage) as a
 * body-level sibling, NOT here — the Figma callout (node 1657-10990) spans the
 * full body width and left-aligns with the other sections, so it must sit
 * outside this card's padding.
 *
 * `flush` (Reborn fixed-slot layout): drop the card surface + padding so the
 * heading left-aligns with the other sections inside its band (Figma node
 * 1657-9469, where the case study sits directly on the #fafafa band). The
 * default (padded card) is kept for the ordered-`blocks` chapters (2727+).
 */

import type { StoryBlock as StoryBlockData } from "../chapter.types";
import { CMF_GUIDE_DEFAULT_IMAGE } from "../cmfGuideData";
import { Quote } from "./Quote";
import "./StoryBlock.css";

interface StoryBlockProps {
  story: StoryBlockData;
  flush?: boolean;
}

export const StoryBlock = ({ story, flush = false }: StoryBlockProps) => (
  <section
    className={`story-block${flush ? " story-block--flush" : ""}`}
    aria-label={story.title}
  >
    <div className="story-block__header">
      <span className="story-block__accent" aria-hidden="true" />
      <h3 className="story-block__title">{story.title}</h3>
    </div>

    <div className="story-block__body">
      <div className="story-block__copy">
        {story.paragraphs.map((paragraph) => (
          <p key={paragraph} className="story-block__paragraph">
            {paragraph}
          </p>
        ))}
        {story.quote && <Quote quote={story.quote} />}
      </div>

      {story.image && (
        <div className="story-block__image-wrap">
          <img
            src={story.image}
            alt={story.title}
            className="story-block__image"
            loading="lazy"
            draggable={false}
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (img.src.endsWith(CMF_GUIDE_DEFAULT_IMAGE)) return;
              img.src = CMF_GUIDE_DEFAULT_IMAGE;
            }}
          />
        </div>
      )}
    </div>
  </section>
);
